import React, { useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TestIds from '../../../testIds'
import {
  Collapse,
  FormControl,
  SelectChangeEvent,
  Stack,
  styled,
  useMediaQuery,
} from '@mui/material'
import { NutritionPromoItem, SelectedPromo } from '../storage'
import { Button } from '../../../components/Button'
import { colors } from '../../../themes/colors'
import { ItemPrice, PriceStyle } from '../../products/components/ProductCard/ProductPriceDetails'
import MaterialSymbol from '../../../components/MaterialSymbol'

import DropdownList from '../../../components/Dropdownlist'
import Input from '../../../components/Input'
import Checkbox from '../../../components/Checkbox'
import TextField from '../../../components/TextField'
import { calculateEndDate, convertMonthToString, spreadEvenly } from '../storage/utils'
import { TDictionary } from '../../../util/types'
import { toFixed } from './util'
import { useSelector } from 'react-redux'
import { selectContactInfo, selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import HelperMessage from '../../../components/HelperMessage'
import { URLS } from '../../../util/consts'

const GreyLabel = styled(Typography)`
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
`

const PromoLineItem: React.FC<{
  selectedDepot: { value: string }
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedPromo[]>>
  isSelected: (item: NutritionPromoItem) => boolean
  getSpreadLabel: (item: NutritionPromoItem) => string
  getPrice: (price: number | null) => number | string
  isSm: boolean
  selectedItems: SelectedPromo[]
  startMonth: Date
  items: NutritionPromoItem[]
}> = ({
  items,
  getPrice,
  getSpreadLabel,
  isSelected,
  isSm,
  selectedDepot,
  selectedItems,
  setSelectedItems,
  startMonth,
}) => {
  const breakpoint = useMediaQuery('@media (max-width: 700px')
  // Find the item with the highest spread
  const selectedAlready = selectedItems.filter((e) => e.name === items[0].name)[0] ?? null

  let itemWithHighestSpread: NutritionPromoItem = items[0]
  items.forEach((item) => {
    if (item.spread !== null && item.spread > (itemWithHighestSpread.spread ?? 0)) {
      itemWithHighestSpread = item
    }
  })

  const [selectedItem, setSelectedItem] = React.useState<NutritionPromoItem>(
    selectedAlready ?? itemWithHighestSpread,
  )

  const item = selectedItem
  const selected = isSelected(item)
  const price = item.prices.find((e) => e.location === selectedDepot.value)?.price

  if (!price) {
    return
  }

  const priceAvail = !!price
  const spreadLabel = getSpreadLabel(item)
  const pricePrefix = ''
  const priceDisplay = pricePrefix + getPrice(price)

  const onChangeSpread = (e: SelectChangeEvent<string>) => {
    const selectedSpread = parseInt(e.target.value.replace(' months', ''))
    const selectedItem = items.find((item) => item.spread === selectedSpread)
    if (selectedItem) {
      setSelectedItems(selectedItems.filter((e) => e.name !== item.name))
      setSelectedItem(selectedItem)
    }
  }
  const spreads = items
    .filter((e) => e !== null)
    .map((e) => ({
      value: e.spread ? e.spread.toString() + ' months' : '',
    }))

  const onSelect = () => {
    let items = [...selectedItems]
    if (selected) {
      items = items.filter((a) => a.key !== item.key)
    } else {
      items.push({
        ...item,
        location: selectedDepot.value,
        spreads: spreadEvenly(item.minQuantity, item.maxSpread ?? 0, startMonth),
        price: price,
        quantity: item.minQuantity,
      })
    }
    setSelectedItems(items)
  }
  return (
    <Box key={item.key} border={`1px solid ${colors.primary100}`} padding={2}>
      <Typography variant='h4' gutterBottom>
        {item.name}
      </Typography>

      <Stack direction={'row'} width={'100%'}>
        <Stack
          direction={!breakpoint ? 'row' : 'column'}
          display='flex'
          justifyContent={'space-between'}
          maxWidth={'100%'}
          flexWrap='wrap'
          spacing={breakpoint ? 2 : 4}
          flexShrink={isSm ? 1 : 0}
        >
          {items.length > 1 ? (
            <Stack spacing={0} minWidth={120}>
              <DropdownList
                items={spreads}
                label='Spread: '
                value={selectedItem.spread ? selectedItem.spread.toString() + ' months' : ''}
                onChange={onChangeSpread}
                testId={TestIds.components.pagination.sortingDropdown}
              />
            </Stack>
          ) : (
            <Stack spacing={0} minWidth={120}>
              <Typography variant='body2' fontWeight={'bold'}>
                Spread:
              </Typography>
              <GreyLabel variant='body2' color={colors.grey700}>
                {spreadLabel}
              </GreyLabel>
            </Stack>
          )}
          <Stack spacing={0} minWidth={80}>
            <Typography variant='body2' fontWeight={'bold'}>
              Minimum order:
            </Typography>
            <GreyLabel variant='body2' color={colors.grey700}>
              {toFixed((item.minQuantity ?? 1) * item.unitMultiple)} {item.unitLabel}
            </GreyLabel>
          </Stack>
          <Stack spacing={0} minWidth={80}>
            <Typography variant='body2' fontWeight={'bold'}>
              Fieldays® offer
            </Typography>
            <ItemPrice
              item={{
                label: 'Fieldays® offer',
                type: PriceStyle.Discounted,
                value: priceDisplay,
              }}
              label={priceAvail ? 'GST excl.' : undefined}
              size='Default'
            />
          </Stack>
        </Stack>
        <Box flexGrow={1} display='flex'>
          <Button
            sx={{
              marginTop: 'auto',
              marginLeft: 'auto',
            }}
            variant={selected ? 'contained' : 'outlined'}
            color={selected ? 'subtle' : 'primary'}
            endIcon={selected ? <MaterialSymbol>check</MaterialSymbol> : null}
            onClick={onSelect}
          >
            {selected ? 'Selected' : 'Select'}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export const StepOne: React.FC<{
  uniqueLocations: { value: string }[]
  selectedDepot: { value: string }
  setSelectedDepot: React.Dispatch<React.SetStateAction<{ value: string }>>
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedPromo[]>>
  promoDictionary: TDictionary<NutritionPromoItem[]>
  isSelected: (item: NutritionPromoItem) => boolean
  getSpreadLabel: (item: NutritionPromoItem) => string
  getPrice: (price: number | null) => number | string
  isSm: boolean
  selectedItems: SelectedPromo[]
  startMonth: Date
}> = (props) => {
  const keys = Object.keys(props.promoDictionary)
  const relationshipOwner = useSelector(selectContactInfo)
  return (
    <Stack spacing={1}>
      <Box maxWidth={200}>
        <DropdownList
          items={props.uniqueLocations}
          label='Bulk site: '
          value={props.selectedDepot.value}
          onChange={(e) => {
            props.setSelectedDepot({ value: e.target.value })
            props.setSelectedItems([])
          }}
          testId={TestIds.components.pagination.sortingDropdown}
        />
      </Box>
      {keys.map((key) => {
        const items = props.promoDictionary[key]

        return <PromoLineItem key={key} {...props} items={items} />
      })}

      <HelperMessage
        title="Can't find what your looking for?"
        body='Contact your Farmlands representative to find out about other promotions that might suit you.'
        show={true}
        name={relationshipOwner.fullName}
        phone={relationshipOwner.phone}
        email={relationshipOwner.email}
        sx={{ m: 2 }}
      />
    </Stack>
  )
}

export const StepTwo: React.FC<{
  selectedItems: SelectedPromo[]
  getPrice: (price: number | null) => number | string
  expanded: string[]
  setExpanded: React.Dispatch<React.SetStateAction<string[]>>
  isSm: boolean
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedPromo[]>>
  pageTwoError: string | undefined
  acceptedTerms: boolean
  setAcceptedTerms: React.Dispatch<React.SetStateAction<boolean>>
  startMonth: Date
}> = ({
  acceptedTerms,
  expanded,
  getPrice,
  isSm,
  pageTwoError,
  selectedItems,
  setAcceptedTerms,
  setExpanded,
  setSelectedItems,
  startMonth,
}) => {
  // Only expand the first item
  useEffect(() => {
    if (expanded.length === 0) {
      setExpanded([selectedItems[0].key])
    }
  }, [])

  const setSpreadForMonth = (
    value: React.SetStateAction<string | undefined>,
    selectedItemsIndex: number,
    spreadIndex: number,
  ) => {
    const updatedPlansSelected = [...selectedItems] // Create a copy of the state array
    const newQuantity = value ? parseInt(value.valueOf() as string) : 0
    updatedPlansSelected[selectedItemsIndex].spreads[spreadIndex].quantity = newQuantity // Update the quantity
    const newTotal = updatedPlansSelected[selectedItemsIndex].spreads.reduce(
      (accumulator, currentSpread) => accumulator + currentSpread.quantity,
      0,
    )
    updatedPlansSelected[selectedItemsIndex].quantity = newTotal
    setSelectedItems(updatedPlansSelected) // Update the state with the new array
  }

  const onExpanded = (expand: boolean, item: SelectedPromo) => {
    if (expand) {
      const temp = expanded.filter((e) => e !== item.key)
      setExpanded(temp)
    } else {
      setExpanded([...expanded, item.key])
    }
  }

  function setTotalAmount(
    selectedItems: SelectedPromo[],
    item: SelectedPromo,
    startMonth: Date,
    setSelectedItems: React.Dispatch<React.SetStateAction<SelectedPromo[]>>,
  ): React.Dispatch<React.SetStateAction<string | undefined>> {
    return (value) => {
      const items = [...selectedItems]
      let quantity = value ? parseInt(value.valueOf() as string) : 0
      if (quantity < 0) {
        quantity = 0
      }
      const spread = spreadEvenly(quantity, item.maxSpread ?? 0, startMonth)
      items[items.indexOf(item)] = {
        ...item,
        quantity: quantity,
        spreads: spread,
      }
      setSelectedItems(items)
    }
  }

  return (
    <Stack spacing={1}>
      {selectedItems.map((item, selectedItemsIndex) => {
        const price = item.price ? item.price * item.quantity : null
        const priceAvail = !!price
        const pricePrefix = ''
        const priceDisplay = pricePrefix + getPrice(price)
        const id = item.key
        const expand = expanded.includes(item.key)

        return (
          <Box key={id} border={`1px solid ${colors.primary100}`} position='relative'>
            <Box onClick={() => onExpanded(expand, item)}>
              <Box sx={{ background: colors.primary100 }} padding={2}>
                <Box display='flex' justifyContent={'space-between'}>
                  <Typography variant='h4' gutterBottom>
                    {item.name}
                    {'  '}
                    <Typography component='span' variant='caption' color='inherit'>
                      {calculateEndDate(startMonth, item.maxSpread ?? 12)}
                    </Typography>
                  </Typography>

                  <MaterialSymbol
                    sx={{
                      height: '22px',
                      width: '22px',
                      transition: 'all 0.2s linear',
                      // Flip the icon so it points from up to down if 'expanded' is true
                      transform: expand ? 'scaleY(-1)' : 'scaleY(1)',
                      cursor: 'pointer',
                    }}
                  >
                    expand_more
                  </MaterialSymbol>
                </Box>

                <Stack direction={'row'} width={'100%'}>
                  <Stack
                    direction={!isSm ? 'row' : 'column'}
                    flexWrap='wrap'
                    spacing={!isSm ? 8 : 0}
                    flexShrink={isSm ? 1 : 0}
                  >
                    <Box maxWidth={'260px'}>
                      <Input
                        label='Total amount:'
                        errorMessage={item.error === 'total' ? item.errorMsg : undefined}
                        inputBaseProps={{
                          type: 'number',
                          pattern: '[0-9]*',
                        }}
                        outlineInputProps={{
                          endAdornment: (
                            <Box>
                              <Typography
                                variant='body2'
                                component='span'
                                fontSize='14px'
                                color={colors.primary500}
                                sx={{ textWrap: 'nowrap' }}
                              >
                                x {item.unitMultiple} {item.unitLabel}
                              </Typography>
                            </Box>
                          ),
                          placeholder: item.minQuantity.toString(),
                          onClick: (e) => e.stopPropagation(),
                        }}
                        setValue={setTotalAmount(selectedItems, item, startMonth, setSelectedItems)}
                        type='number'
                        testId={item.key}
                        value={item.quantity.toString()}
                      />
                    </Box>
                    <Stack spacing={0} minWidth={80}>
                      <Stack spacing={2} direction={'row'}>
                        <Stack spacing={1} minWidth={80}>
                          <Typography variant='body2' fontWeight={'bold'} mb={'4px'}>
                            Units{' '}
                          </Typography>
                          <Typography
                            sx={{ height: '45px' }}
                            variant='body1'
                            color={colors.primary500}
                          >
                            <Typography
                              component='span'
                              fontWeight={'bold'}
                              color={colors.primary500}
                            >
                              {toFixed(item.unitMultiple * item.quantity)} {item.unitLabel}
                            </Typography>
                          </Typography>
                        </Stack>

                        <Stack spacing={1} minWidth={60}>
                          <Typography variant='body2' fontWeight={'bold'} mb={'4px'} height='24px'>
                            {' '}
                          </Typography>
                          <Typography
                            sx={{ height: '45px' }}
                            variant='body1'
                            color={colors.primary500}
                          >
                            <Typography
                              component='span'
                              fontWeight={'bold'}
                              color={colors.primary500}
                            >
                              =
                            </Typography>
                          </Typography>
                        </Stack>

                        <Stack spacing={1} minWidth={80}>
                          <Typography variant='body2' fontWeight={'bold'} mb={'4px'}>
                            Total
                          </Typography>
                          <Typography
                            sx={{ height: '45px' }}
                            variant='body1'
                            color={colors.primary500}
                          >
                            <Typography
                              component='span'
                              fontWeight={'bold'}
                              color={colors.primary500}
                            >
                              {priceDisplay}
                            </Typography>
                            <Typography component='span' fontSize='12px' color={colors.primary500}>
                              {priceAvail ? ' GST excl.' : undefined}
                            </Typography>
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            </Box>

            <Box
              flexGrow={1}
              display='flex'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <Collapse in={expand}>
                <Box padding={2} display='grid' gridTemplateColumns='repeat(3, 1fr)' gap={2}>
                  {item.spreads.map((spread, spreadIndex) => {
                    const minError = item.error === 'min' ? item.errorMsg : undefined
                    const maxError = item.error === 'max' ? item.errorMsg : undefined
                    let errorIndex = undefined
                    if (minError) {
                      errorIndex = spread.quantity <= 0 ? spreadIndex : undefined
                    } else if (maxError) {
                      errorIndex = getErrorIndex(item, errorIndex)
                    }

                    return (
                      <Box key={spread.month.toISOString()}>
                        <Input
                          label={convertMonthToString(spread.month)}
                          setValue={(value) =>
                            setSpreadForMonth(value, selectedItemsIndex, spreadIndex)
                          }
                          type='number'
                          errorMessage={errorIndex === spreadIndex ? ' ' : undefined}
                          inputBaseProps={{
                            type: 'number',
                            pattern: '[0-9]*',
                          }}
                          testId={item.key}
                          value={spread.quantity.toString()}
                        />
                      </Box>
                    )
                  })}

                  {item.error && ['max', 'min'].includes(item.error) && (
                    <Typography variant='body2' color={colors.error500}>
                      {item.errorMsg}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        )
      })}

      <FormControl error={!!pageTwoError}>
        <Checkbox
          color={pageTwoError && !acceptedTerms ? colors.error500 : undefined}
          label={
            <>
              I accept the{' '}
              <Typography
                component='a'
                variant='link'
                href={URLS.termsAndConditions}
                target='blank'
              >
                terms and conditions
              </Typography>{' '}
              for these spreads
            </>
          }
          checked={acceptedTerms}
          onChange={(_, checked) => setAcceptedTerms(checked)}
          data-testid={TestIds.components.address.saveAddress}
        />
        {pageTwoError && !acceptedTerms && (
          <Typography variant='body2' color={'error'}>
            Please accept the terms of the spread to continue
          </Typography>
        )}
      </FormControl>
    </Stack>
  )
}

export const StepThree: React.FC<{
  priceDisplay: string
  containsPrice: SelectedPromo | undefined
  containsPOA: SelectedPromo | undefined
  isSm: boolean
  selectedItems: SelectedPromo[]
  email: string | undefined
  setEmail: React.Dispatch<React.SetStateAction<string | undefined>>
  emailError: string | undefined
  phone: string | undefined
  setPhone: React.Dispatch<React.SetStateAction<string | undefined>>
  phoneError: string | undefined
  setNotes: React.Dispatch<React.SetStateAction<string | undefined>>
  notes: string | undefined
}> = ({
  containsPOA,
  containsPrice,
  email,
  emailError,
  isSm,
  notes,
  phone,
  phoneError,
  priceDisplay,
  selectedItems,
  setEmail,
  setNotes,
  setPhone,
}) => {
  const account = useSelector(selectCurrentFarmerAccount)
  const isTfo = account?.hasTfoAccess

  return (
    <Stack spacing={1}>
      <Box border={`1px solid ${colors.primary300}`} position='relative'>
        <Box>
          <Box sx={{ background: colors.primary100 }} padding={2}>
            <Box display='block'>
              <Box display='flex' justifyContent={'space-between'}>
                <Typography variant='h4' gutterBottom>
                  Summary
                </Typography>
                <Typography sx={{ height: '45px' }} variant='body1' color={colors.primary500}>
                  <Typography component='span' fontWeight={'bold'} color={colors.primary500}>
                    {priceDisplay}
                  </Typography>
                  <Typography component='span' fontSize='12px' color={colors.primary500}>
                    {containsPrice ? ' GST excl.' : undefined}
                  </Typography>
                </Typography>
              </Box>

              {containsPOA && (
                <Typography
                  sx={{ float: 'right', height: '45px' }}
                  variant='body1'
                  color={colors.primary500}
                >
                  <Typography component='span' fontWeight={'bold'} color={colors.primary500}>
                    + Additional Priced on Application
                  </Typography>
                </Typography>
              )}
            </Box>

            <Stack direction={'row'} width={'100%'}>
              <Stack flexWrap='wrap' spacing={1} flexShrink={isSm ? 1 : 0}>
                {selectedItems.map((e) => (
                  <Typography key={e.key} variant='h4'>
                    <Typography component='span' fontWeight='bold' color={colors.primary500}>
                      {e.name}{' '}
                    </Typography>
                    <Typography component='span' color={colors.primary500}>
                      {toFixed(e.quantity * e.unitMultiple)} {e.unitLabel}{' '}
                    </Typography>
                    <Typography component='span' fontWeight='bold' color={colors.primary500}>
                      {e.price ? '@ $' + e.price.toString() : ' - POA'}
                    </Typography>
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Box
        flexGrow={1}
        display='flex'
        flexDirection='column'
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <Box
          mt={3}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            columnGap: '16px', // Adjust the gap as needed
            width: '100%',
          }}
        >
          <Box minWidth={'300px'} flexGrow={1}>
            <Input
              label={isTfo ? 'Customer email address' : 'Email address'}
              required
              value={email}
              setValue={setEmail}
              errorMessage={emailError}
              type='email'
              testId={TestIds.components.contactInfo.emailInput}
            />
          </Box>
          <Box minWidth={'300px'} flexGrow={1}>
            <Input
              label={isTfo ? 'Customer phone number' : 'Phone number'}
              required
              value={phone}
              setValue={setPhone}
              errorMessage={phoneError}
              type='tel'
              inputBaseProps={{ pattern: '[^\\s]*', type: 'tel' }}
              testId={TestIds.components.contactInfo.phoneInput}
            />
          </Box>
        </Box>

        <TextField
          variant='outlined'
          label='Notes or questions:'
          testId={TestIds.components.address.instructionsField}
          onChange={(e) => {
            setNotes(e.target.value)
          }}
          inputProps={{
            maxLength: 200,
          }}
          value={notes}
          data-testid='delivery-input'
          helperText={`${notes ? notes.length : 0}/200 characters`}
          helperTextColor={colors.grey700}
          multiline
          maxRows={10}
          minRows={3}
        />
      </Box>
    </Stack>
  )
}

function getErrorIndex(item: SelectedPromo, errorIndex: number | undefined) {
  for (let i = item.spreads.length - 1; i >= 0 && !errorIndex; i++) {
    if (item.spreads[i].quantity) {
      errorIndex = i
      break
    }
  }
  return errorIndex
}
